/**
 * FigmaBetCart — 300px right panel (Figma 418:2021)
 * 3-zone: tabs | items | footer (sticky bottom)
 */
import React, { useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Trash2, ShoppingCart, Eraser, Plus, Minus } from 'lucide-react';
import { SportsBetCartContext } from '../../../_context/SportsBetCartContext';
import { SportsContext } from '../../../_context/SportsContext';
import SportsnewContext from '../../../_context/SportsnewContext';
import { SettingContext } from '../../../_context/setting.contax';
import { useWalletSummary } from '../../../_context/wallet.context';
import { formatOdds, toDisplayOddsDecimal } from '../_helpers/oddsFormat';
import useServiceContextHook from '../../../_hooks/service.context.hook';
import { notify } from '../../../_helpers/notify';
import { getMarketTitle } from './marketTitleResolver';
import { getSelectionLabel } from './selectionLabelResolver';
import { computeBetCartMaxStake, resolveAvailableBalance, validateBetCartLimits } from '../_helpers/betCartStakeLimits';
import { waitForPendingBetResolution } from '../_helpers/inplayDelayFlow';
import { extractBonusRecoveryGate } from '../_helpers/bonusRecovery';
import {
  recoverAfterSportsnewBetError,
  recoverAfterSportsnewBonusConfirmError,
} from '../_helpers/betErrorResync';
import { getSportsnewBetErrorMessage } from '../_helpers/sportsnewBetErrorMessage';
import { normalizeSportsnewBetPlacementResponse } from '../_helpers/sportsnewBetPlacementResponse';
import { dispatchOptimisticSportsbookHistoryRow } from '../../../features/sportsbookHistory/optimistic/sportsbookHistoryOptimisticRows.optimistic';
import { isSportsbookUnifiedHistoryEnabled } from '../../../features/sportsbookHistory/policies/sportsbookHistory.policy';
import {
  clearPendingInplayBet,
  persistPendingInplayBet,
  readPendingInplayBet,
} from '../_helpers/sportsPageState';
import useBetQuotePreview from '../_hooks/useBetQuotePreview';
import BonusRecoveryModal from '../../../components/CommonModal/BonusRecoveryModal';
import SportsBetSuccessModal from '../../../components/CommonModal/SportsBetSuccessModal';
const BUCKET = { DOMESTIC:'domestic', INTERNATIONAL:'international', SPECIAL:'special', LIVE:'live' };
const DISPLAY_SCOPE_BY_CATEGORY = {
  DOMESTIC: 'domestic',
  INTERNATIONAL: 'overseas',
  SPECIAL: 'esports',
  LIVE: 'live',
};
const fmt = n => Number(n).toLocaleString('ko-KR');
const extractWalletPayload = (response) => {
  const payload = response?.data && typeof response.data === 'object' ? response.data : response;
  const walletSummary = payload?.walletSummary || payload?.wallet_summary;
  const balanceAfter = payload?.balanceAfter ?? payload?.balance_after;
  if (walletSummary) return walletSummary;
  if (balanceAfter != null) return { available_balance: balanceAfter };
  return null;
};
const normalizeBonusTiers = (source) => {
  const raw = source?.comboBonusTiers
    || source?.combo_bonus_tiers
    || source?.tiers
    || source?.comboBonus?.tiers
    || source?.combo_bonus?.tiers
    || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((tier) => {
      const fold = Number(tier.fold ?? tier.min_legs ?? tier.minLegs);
      const value = Number(tier.value ?? tier.multiplier);
      const rate = Number(tier.ratePercent ?? tier.rate_percent);
      const resolvedValue = Number.isFinite(value) && value >= 1
        ? value
        : Number((1 + (Number.isFinite(rate) ? rate : 0) / 100).toFixed(4));
      if (!Number.isFinite(fold) || fold < 2 || !Number.isFinite(resolvedValue) || resolvedValue <= 1) {
        return null;
      }
      return { ...tier, fold, value: resolvedValue };
    })
    .filter(Boolean)
    .sort((a, b) => a.fold - b.fold);
};

// FNV-1a 32-bit hash — fast, no dependencies, sufficient for idempotency keys
const fnv1a = (str) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};

const generateIdempotencyKey = (userId, betMode, selections, stake) => {
  const selectionPart = selections
    .map(item => ({
      oddStateId: Number(item.odds_id),
      price: toDisplayOddsDecimal(item.odds) ?? Number(item.odds),
    }))
    .sort((a, b) => a.oddStateId - b.oddStateId)
    .map(item => `${item.oddStateId}_${item.price}`)
    .join('-');

  const randomPart =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2)}`;

  return [
    'sportsnew',
    userId,
    betMode,
    Number(stake),
    selectionPart,
    Date.now(),
    randomPart,
  ]
    .join('-')
    .replace(/[^A-Za-z0-9._-]/g, '-')
    .slice(0, 100);
};

const FigmaBetCart = ({
  isMobile = false,
  mobileOpen = false,
  onMobileClose = () => {},
}) => {
  const navigate = useNavigate();
  const {
    selections, stake, setStake, removeSelection, clearCart, replaceSelectionsForType,
    totalOdds, currentType, setCurrentType,
    updateOddsInCart, oddsChanges, setBonusMultiplier, acceptOddsChange
  } = useContext(SportsBetCartContext);
  const { bettingType, displaySettings, forceResyncOdds } = useContext(SportsContext);
  const {
    comboBonus: bonusTiers,
    comboBuckets: bonusBuckets,
    displayScope,
    recoverSelectedOddsForCart,
  } = useContext(SportsnewContext);
  const { userdetail } = useContext(SettingContext);
  const { walletSummary, refreshWalletSummary, applyWalletSummary } = useWalletSummary();
  const _fmt = displaySettings?.default_odds_format || 'decimal';
  const { session, services: { SportsService, SportsnewService } } = useServiceContextHook();
  const activeCategory = currentType || bettingType || 'DOMESTIC';
  const activeDisplayScope = displayScope || DISPLAY_SCOPE_BY_CATEGORY[activeCategory] || 'domestic';
  const betMode = activeCategory === 'LIVE' ? 'live' : 'prematch';
  const betDetailService = SportsnewService || SportsService;
  const [resolvedBalance, setResolvedBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const userdetailRef = useRef(userdetail);
  const walletSummaryRef = useRef(walletSummary);
  const availableBalance = useMemo(
    () => resolveAvailableBalance(
      resolvedBalance,
      walletSummary,
      userdetail,
      session?.user
    ),
    [resolvedBalance, walletSummary, userdetail, session?.user]
  );

  useEffect(() => {
    userdetailRef.current = userdetail;
  }, [userdetail]);

  useEffect(() => {
    walletSummaryRef.current = walletSummary;
  }, [walletSummary]);

  useEffect(() => {
    if (!session?.user) {
      setResolvedBalance(null);
      return;
    }

    setResolvedBalance(resolveAvailableBalance(walletSummary, userdetail, session?.user));
  }, [walletSummary, userdetail, session?.user?.id]);

  const [bettingLimits, setBettingLimits] = useState(null);
  const [bettingLimitsLoading, setBettingLimitsLoading] = useState(false);

  const scopedBonusTiers = useMemo(() => normalizeBonusTiers(bettingLimits), [bettingLimits]);

  const effectiveTiers = useMemo(() => {
    if (scopedBonusTiers.length > 0) return scopedBonusTiers;
    const b = BUCKET[bettingType];
    return (b && bonusBuckets[b]?.length > 0) ? bonusBuckets[b] : bonusTiers;
  }, [bettingType, bonusTiers, bonusBuckets, scopedBonusTiers]);

  const betType = selections.length <= 1 ? 'SINGLE' : 'COMBO';
  const activeTier = useMemo(() => {
    if (betType !== 'COMBO' || selections.length < 3 || !effectiveTiers?.length) return null;
    return effectiveTiers.filter(t => selections.length >= t.fold).sort((a, b) => b.fold - a.fold)[0] || null;
  }, [betType, selections.length, effectiveTiers]);
  const activeBonusMultiplier = activeTier ? Number(activeTier.value) : 1;

  useEffect(() => { setBonusMultiplier?.(activeBonusMultiplier); }, [activeBonusMultiplier, setBonusMultiplier]);
  useEffect(() => { setCurrentType(bettingType); }, [bettingType, setCurrentType]);
  const sportsnewServiceReady = Boolean(SportsnewService);
  useEffect(() => {
    if (!sportsnewServiceReady) return undefined;
    let cancelled = false;
    setBettingLimitsLoading(true);
    SportsnewService.getBettingLimits({
      betting_category: bettingType || 'DOMESTIC',
      bet_type: betType,
      scope: activeDisplayScope,
      displayScope: activeDisplayScope,
    }).then(res => {
      if (!cancelled) setBettingLimits(res?.data || null);
    }).catch(() => {
      if (!cancelled) setBettingLimits(null);
    }).finally(() => {
      if (!cancelled) setBettingLimitsLoading(false);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sportsnewServiceReady, bettingType, betType, activeDisplayScope, session?.user?.id]);

  const refreshAvailableBalance = useCallback(async () => {
    if (!session?.user) {
      setResolvedBalance(null);
      return 0;
    }
    setBalanceLoading(true);
    try {
      const summary = await refreshWalletSummary({ force: true });
      const nextBalance = resolveAvailableBalance(summary, walletSummaryRef.current, userdetailRef.current, session?.user);
      setResolvedBalance(nextBalance);
      return nextBalance;
    } catch (error) {
      const fallbackBalance = resolveAvailableBalance(walletSummaryRef.current, userdetailRef.current, session?.user);
      setResolvedBalance(fallbackBalance);
      return fallbackBalance;
    } finally {
      setBalanceLoading(false);
    }
  }, [refreshWalletSummary, session?.user?.id]);

  useEffect(() => {
    if (!session?.user) {
      setResolvedBalance(null);
      return undefined;
    }
    refreshAvailableBalance();
  }, [session?.user?.id, refreshAvailableBalance]);

  const [submitting, setSubmitting] = useState(false);
  const [pendingBetState, setPendingBetState] = useState(null);
  const [showBonusRecoveryModal, setShowBonusRecoveryModal] = useState(false);
  const [showBetSuccessModal, setShowBetSuccessModal] = useState(false);
  const [bonusActionType, setBonusActionType] = useState('RECOVER');
  const [bonusGames, setBonusGames] = useState('sport');
  const [bonusRecoveryRequest, setBonusRecoveryRequest] = useState(null);
  const isMountedRef = useRef(true);
  const pendingBetFlowRef = useRef(null);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (updateOddsInCart) {
      window.dispatchEvent(new CustomEvent('sp_cart_sync', { detail: { updateOddsInCart } }));
    }
  }, [updateOddsInCart]);

  const {
    displayedBaseOdds,
    displayedBonusMultiplier,
    comboOddsAdjust,
    displayedFinalOdds,
    displayedPotentialWin,
    shouldShowFinalOdds,
  } = useBetQuotePreview({
    SportsService: SportsnewService,
    selections,
    betType,
    bettingCategory: activeCategory,
    displayScope: activeDisplayScope,
    betMode,
    isSportsnew: true,
    stake,
    totalOdds,
    bonusMultiplier: activeBonusMultiplier,
    bettingLimits,
    isAuthenticated: !!session?.user,
  });
  const maxStakeInfo = useMemo(() => computeBetCartMaxStake({
    balance: availableBalance,
    betType,
    bettingLimits,
    displayedFinalOdds,
  }), [availableBalance, betType, bettingLimits, displayedFinalOdds]);

  const recoverAfterBetRejection = useCallback((source, submittedSelections) => (
    recoverAfterSportsnewBetError({
      source,
      submittedSelections,
      displayScope: activeDisplayScope,
      recoverSelectedOddsForCart,
      forceResyncOdds,
    })
  ), [activeDisplayScope, forceResyncOdds, recoverSelectedOddsForCart]);

  const runPendingBetFlow = useCallback(async (pendingBet) => {
    if (!pendingBet?.betId || !isMountedRef.current) return;
    if (pendingBetFlowRef.current === pendingBet.betId) return;

    pendingBetFlowRef.current = pendingBet.betId;

    if (pendingBet.bettingCategory === 'LIVE') {
      setCurrentType('LIVE');
    }

    if (Array.isArray(pendingBet.selections) && pendingBet.selections.length > 0) {
      replaceSelectionsForType?.('LIVE', pendingBet.selections);
    }

    if (pendingBet.stake !== '' && pendingBet.stake != null) {
      setStake(String(pendingBet.stake));
    }

    setPendingBetState({
      betId: pendingBet.betId,
      secondsLeft: pendingBet.secondsLeft,
    });

    try {
      await refreshAvailableBalance();
      const resolvedBet = await waitForPendingBetResolution({
        SportsService: betDetailService,
        betId: pendingBet.betId,
        delaySeconds: pendingBet.secondsLeft,
        isCancelled: () => !isMountedRef.current,
        onTick: (secondsLeft) => {
          setPendingBetState((prev) => (
            prev ? { ...prev, secondsLeft } : prev
          ));
        },
      });

      if (!isMountedRef.current) return;

      setPendingBetState(null);
      await refreshAvailableBalance();

      if (!isMountedRef.current) return;

      if (!resolvedBet) {
        notify('인플레이 베팅 결과 확인이 지연되고 있습니다. 내 베팅에서 상태를 확인해주세요.', 'error');
        return;
      }

      clearPendingInplayBet();

      if (resolvedBet.status === 'CONFIRMED') {
        replaceSelectionsForType?.('LIVE', []);
        setStake('');
        onMobileClose?.();
        setShowBetSuccessModal(true);
        return;
      }

      if (Array.isArray(pendingBet.selections) && pendingBet.selections.length > 0) {
        replaceSelectionsForType?.('LIVE', pendingBet.selections);
        setCurrentType('LIVE');
        if (pendingBet.stake !== '' && pendingBet.stake != null) {
          setStake(String(pendingBet.stake));
        }
        await recoverAfterBetRejection(resolvedBet, pendingBet.selections);
      }

      notify(
        resolvedBet?.void_reason || '배당 변동 또는 마켓 상태 변경으로 인플레이 베팅이 거부되었습니다',
        'error'
      );
    } catch (error) {
      if (!isMountedRef.current) return;
      setPendingBetState(null);
      notify('인플레이 베팅 상태 확인 중 오류가 발생했습니다. 내 베팅에서 상태를 확인해주세요.', 'error');
    } finally {
      pendingBetFlowRef.current = null;
    }
  }, [
    betDetailService,
    onMobileClose,
    recoverAfterBetRejection,
    refreshAvailableBalance,
    replaceSelectionsForType,
    setCurrentType,
    setStake,
  ]);

  useEffect(() => {
    if (activeCategory !== 'LIVE') return;

    const pendingBet = readPendingInplayBet();
    if (!pendingBet) return;

    runPendingBetFlow(pendingBet);
  }, [activeCategory, runPendingBetFlow]);

  const closeBonusRecoveryModal = useCallback(() => {
    setShowBonusRecoveryModal(false);
    setBonusRecoveryRequest(null);
  }, []);

  const closeBetSuccessModal = useCallback(() => {
    setShowBetSuccessModal(false);
  }, []);

  const submitBetRequest = useCallback(async ({ payload, submittedSelections }) => {
    const requestStake = Number(payload?.stake ?? stake);
    const res = await SportsnewService.placeBet(payload);

    if (!isMountedRef.current) return;

    const bonusGate = extractBonusRecoveryGate(res);
    if (bonusGate) {
      setBonusGames(bonusGate.bonusGames);
      setBonusActionType(bonusGate.actionType);
      setBonusRecoveryRequest({ payload, submittedSelections });
      setShowBonusRecoveryModal(true);
      return;
    }

    if (res?.status) {
      const walletPayload = extractWalletPayload(res);
      if (walletPayload) {
        const nextWallet = applyWalletSummary(walletPayload);
        setResolvedBalance(resolveAvailableBalance(nextWallet, walletPayload, walletSummaryRef.current, userdetailRef.current, session?.user));
      }
      const betResponse = normalizeSportsnewBetPlacementResponse(res);
      if (betMode === 'live' && betResponse.isPendingLive) {
        persistPendingInplayBet({
          betId: betResponse.betId,
          delaySeconds: betResponse.delaySeconds,
          delayExpiresAt: betResponse.delayExpiresAt,
          selections: submittedSelections,
          stake: requestStake,
          bettingCategory: activeCategory,
        });
        await runPendingBetFlow(
          readPendingInplayBet() || {
            betId: betResponse.betId,
            bettingCategory: activeCategory,
            selections: submittedSelections,
            stake: String(requestStake),
            secondsLeft: betResponse.delaySeconds,
          }
        );
        return;
      }

      if (betResponse.historyRow && isSportsbookUnifiedHistoryEnabled()) {
        dispatchOptimisticSportsbookHistoryRow(betResponse.historyRow);
      }
      setShowBetSuccessModal(true);
      clearCart();
      return;
    }

    const failMsg = getSportsnewBetErrorMessage(res, '베팅 실패');
    notify(failMsg, 'error');
    await recoverAfterBetRejection(res, submittedSelections);
  }, [
    SportsnewService,
    activeCategory,
    applyWalletSummary,
    betMode,
    clearCart,
    recoverAfterBetRejection,
    runPendingBetFlow,
    session?.user,
    stake,
  ]);

  const handleBonusRecoveryConfirm = useCallback(async () => {
    if (!bonusRecoveryRequest) return;
    setShowBonusRecoveryModal(false);
    setSubmitting(true);
    try {
      await submitBetRequest({
        payload: {
          ...bonusRecoveryRequest.payload,
          is_bonus_recover: true,
        },
        submittedSelections: bonusRecoveryRequest.submittedSelections,
      });
    } catch (error) {
      const errMsg = getSportsnewBetErrorMessage(error, '베팅 오류');
      notify(errMsg, 'error');
      await recoverAfterSportsnewBonusConfirmError({
        source: error,
        bonusRecoveryRequest,
        fallbackSelections: selections,
        displayScope: activeDisplayScope,
        recoverSelectedOddsForCart,
        forceResyncOdds,
      });
    } finally {
      setBonusRecoveryRequest(null);
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [
    activeDisplayScope,
    bonusRecoveryRequest,
    forceResyncOdds,
    recoverSelectedOddsForCart,
    selections,
    submitBetRequest,
  ]);

  const handlePlace = async () => {
    if (!session?.user) { notify('로그인이 필요합니다', 'error'); return; }
    if (selections.length === 0) { notify('선택지를 추가하세요', 'error'); return; }
    // Never submit with an unresolved lock/odds-change — the user must accept
    // the new price or remove the selection first (see acceptOddsChange).
    const unresolved = selections.find((s) => s.status && s.status !== 'active');
    if (unresolved) {
      const msg = unresolved.status === 'odds_changed'
        ? '배당이 변경된 항목이 있습니다. 변경사항을 수락하거나 삭제해주세요'
        : '베팅 불가능한 항목이 있습니다. 삭제 후 다시 시도해주세요';
      notify(msg, 'error');
      return;
    }
    if (!stake || Number(stake) <= 0) { notify('베팅 금액을 입력하세요', 'error'); return; }
    if (!bettingLimitsLoading && Number(stake) < maxStakeInfo.limits.global_min_bet) {
      notify(`최소 베팅액은 ${fmt(maxStakeInfo.limits.global_min_bet)}원입니다`, 'error');
      return;
    }
    if (!bettingLimitsLoading) {
      const limitCheck = validateBetCartLimits({
        selections,
        stake,
        bettingLimits,
        displayedFinalOdds,
        maxStakeInfo,
      });
      if (!limitCheck.ok) {
        notify(limitCheck.message, 'error');
        return;
      }
    }
    const submittedSelections = selections.map((selection) => ({ ...selection }));
    setSubmitting(true);
    try {
      const idempotencyKey = generateIdempotencyKey(
        session.user.id,
        betMode,
        selections,
        Number(stake),
      );
      const payload = {
        bet_type: betType, stake: Number(stake),
        betting_category: activeCategory,
        displayScope: activeDisplayScope,
        scope: activeDisplayScope,
        betMode,
        idempotencyKey: idempotencyKey,
        selections: selections.map(s => ({
          match_id: s.match_id,
          oddStateId: s.odds_id,
          providerOddId: s.providerOddId ?? s.provider_odd_id ?? null,
          oddIdentityKey: s.oddIdentityKey ?? s.odd_identity_key ?? null,
          shortOddId: s.shortOddId ?? null,
          canonicalSelectionKey: s.canonicalSelectionKey ?? null,
          canonicalLineKey: s.canonicalLineKey ?? null,
          canonicalPairKey: s.canonicalPairKey ?? null,
          points: s.points ?? s.line ?? null,
          line: s.line ?? s.points ?? null,
          selectionLine: s.selectionLine ?? null,
          groupingKey: s.groupingKey ?? null,
          expectedPriceDecimal: toDisplayOddsDecimal(s.odds) ?? Number(s.odds),
        })),
      };
      await submitBetRequest({ payload, submittedSelections });
    } catch (e) {
      const errMsg = getSportsnewBetErrorMessage(e, '베팅 오류');
      notify(errMsg, 'error');
      await recoverAfterBetRejection(e, submittedSelections);
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  };

  const handleMaxStake = async () => {
    if (!session?.user) {
      notify('로그인이 필요합니다', 'error');
      return;
    }
    if (bettingLimitsLoading || balanceLoading) {
      notify('최대 가능 금액 계산 중입니다. 잠시 후 다시 시도해주세요', 'error');
      return;
    }
    let nextMaxStake = maxStakeInfo.maxStake;
    let nextMinStake = maxStakeInfo.limits.global_min_bet;
    if (nextMaxStake <= 0) {
      const freshBalance = await refreshAvailableBalance();
      const refreshed = computeBetCartMaxStake({
        balance: freshBalance,
        betType,
        bettingLimits,
        displayedFinalOdds,
      });
      nextMaxStake = refreshed.maxStake;
      nextMinStake = refreshed.limits.global_min_bet;
    }
    if (nextMaxStake <= 0) {
      notify('보유금이 없거나 잔액 정보를 아직 불러오지 못했습니다', 'error');
      return;
    }
    if (nextMaxStake < nextMinStake) {
      setStake('');
      notify(`현재 최대 가능 금액이 최소 베팅액 ${fmt(nextMinStake)}원보다 작습니다`, 'error');
      return;
    }
    setStake(String(nextMaxStake));
  };

  const handleIncrement = () => {
    setStake(p => {
      const current = Number(p) || 0;
      return String(current + 10000);
    });
  };

  const handleDecrement = () => {
    setStake(p => {
      const current = Number(p) || 0;
      return String(Math.max(0, current - 10000));
    });
  };

  const addAmt = v => v === 'max'
    ? handleMaxStake()
    : setStake(p => String((Number(p) || 0) + v));

  const comboAdjustText = comboOddsAdjust === 0
    ? '0.00'
    : `${comboOddsAdjust > 0 ? '+' : '-'}${Math.abs(comboOddsAdjust).toFixed(2)}`;

  const cartPanel = (
    <div className={`zxsb_cart_wrap ${isMobile ? 'zxsb_cart_wrap_mobile' : ''}`}>
      {/* ── Zone 1: Tabs ── */}
      <div className="zxsb_cart_tabs">
        <button type="button" className="zxsb_cart_tab zxsb_cart_tab_active">
          베팅카트 {selections.length > 0 && <span className="zxsb_cart_badge">{selections.length}</span>}
        </button>
        <button type="button" className="zxsb_cart_tab" onClick={() => navigate('/betting-history?tab=Sportsbook')}>
          내 베팅
        </button>
      </div>

      {/* ── Zone 2: Summary + Items ── */}
      <div className="zxsb_cart_body">
        <div className="zxsb_cart_sumbar">
          <span>선택 목록 ({selections.length})</span>
        </div>

        <div className="zxsb_cart_items">
          {selections.length === 0 ? (
            <div className="zxsb_cart_empty">
              <ShoppingCart size={28} strokeWidth={1.5} />
              <span>베팅 카트가 비어있습니다</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>배당을 클릭하여 추가하세요</span>
            </div>
          ) : (
            selections.map(sel => {
              const isLocked = sel.status === 'locked';
              const isUnavailable = sel.status === 'unavailable';
              const isOddsChanged = sel.status === 'odds_changed';
              return (
              <div key={sel.odds_id} className="zxsb_cart_card" style={(isLocked || isUnavailable) ? { opacity: 0.55 } : undefined}>
                <div className="zxsb_cart_card_top">
                  <span className="zxsb_cart_card_match">{sel.match_name}</span>
                  <button type="button" className="zxsb_cart_card_x" onClick={() => removeSelection(sel.odds_id)}><X size={12} /></button>
                </div>
                <div className="zxsb_cart_card_mid">
                  {sel.market_title_display_ko
                    || sel.market_name_ko
                    || getMarketTitle(sel.market_title_display)
                    || getMarketTitle(sel.market_name)
                    || sel.market_title_display
                    || sel.market_name}
                  {sel.market_line ? ` (${String(sel.market_line).replace(/\s*\(0-0\)\s*$/, '')})` : ''}
                </div>
                <div className="zxsb_cart_card_bot">
                  <span className="zxsb_cart_card_sel">{
                    sel.selection_name_display
                      || getSelectionLabel(
                        { name: sel.selection_name },
                        { marketName: sel.market_title_display_ko || sel.market_name_ko || sel.market_title_display || sel.market_name, match: sel }
                      )
                  }</span>
                  <span className={`zxsb_cart_card_odds ${oddsChanges?.[sel.odds_id] === 'up' ? 'zxsb_delta_up' : oddsChanges?.[sel.odds_id] === 'down' ? 'zxsb_delta_down' : ''}`}>
                    {formatOdds(sel.odds, _fmt)}
                  </span>
                </div>
                {/* Persistent status overlay — never auto-applied, requires explicit
                    accept or manual removal (see acceptOddsChange / removeSelection). */}
                {(isLocked || isUnavailable || isOddsChanged) && (
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      marginTop: 6, padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                      background: isOddsChanged ? 'rgba(89, 244, 119, 0.12)' : 'rgba(166, 196, 213, 0.12)',
                      color: isUnavailable ? '#ef4444' : (isLocked ? '#a6c4d5' : 'var(--sb-primary)'),
                    }}
                  >
                    <span>
                      {isUnavailable && '이용 불가'}
                      {isLocked && !isUnavailable && '배당 잠김'}
                      {isOddsChanged && `배당 변경: ${formatOdds(sel.odds, _fmt)} → ${formatOdds(sel.pendingOdds, _fmt)}`}
                    </span>
                    {isOddsChanged && (
                      <button
                        type="button"
                        onClick={() => acceptOddsChange(sel.odds_id)}
                        style={{ background: 'var(--sb-primary)', color: '#0b1c24', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                      >
                        수락
                      </button>
                    )}
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Zone 3: Footer (sticky bottom) ── */}
      <div className="zxsb_cart_foot">
        {/* Secondary Info Rows */}
        <div className="zxsb_cart_info_group_secondary">
          <div className="zxsb_cart_frow_sec">
            <span>보유금</span>
            <span>{balanceLoading && availableBalance <= 0 ? '조회중' : fmt(availableBalance || 0)}원</span>
          </div>
          {selections.length > 0 && (
            <>
              <div className="zxsb_cart_frow_sec">
                <span>합산배당</span>
                <span>{formatOdds(displayedBaseOdds, _fmt)}x</span>
              </div>
              <div className="zxsb_cart_frow_sec">
                <span>보너스배당</span>
                <span style={{ color: activeTier ? '#e5a913' : 'inherit', fontWeight: activeTier ? 700 : 'normal' }}>
                  x{Number(displayedBonusMultiplier).toFixed(2)}
                </span>
              </div>
              {Math.abs(comboOddsAdjust) > 0.001 && (
                <div className="zxsb_cart_frow_sec">
                  <span>조합배당조정</span>
                  <span style={{ color: comboOddsAdjust > 0 ? 'var(--sb-primary)' : '#ef4444' }}>
                    {comboAdjustText}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* 모두비우기 */}
        <div className="zxsb_cart_clear_row">
          <button type="button" className="zxsb_cart_clear_all_btn" onClick={clearCart}>
            <Trash2 size={16} className="zxsb_cart_clear_all_icon" />
            <span>모두비우기</span>
          </button>
        </div>

        {/* + / - 베팅금액 Eraser */}
        <div className="zxsb_cart_stake_row_new">
          <div className="zxsb_cart_adjust_group">
            <button type="button" className="zxsb_cart_adjust_btn" onClick={handleIncrement}>
              <Plus size={14} />
            </button>
            <button type="button" className="zxsb_cart_adjust_btn" onClick={handleDecrement}>
              <Minus size={14} />
            </button>
          </div>
          <div className="zxsb_cart_input_wrapper">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9,]*"
              className="zxsb_cart_amount_input"
              placeholder="베팅금액"
              value={stake ? fmt(Number(stake)) : ''}
              onChange={e => { const raw = e.target.value.replace(/[^0-9]/g, ''); setStake(raw); }}
            />
            <button type="button" className="zxsb_cart_input_clear" onClick={() => setStake('')}>
              <Eraser size={16} />
            </button>
          </div>
        </div>

        {/* Preset Buttons */}
        <div className="zxsb_cart_presets">
          <button type="button" className="zxsb_cart_preset_btn" onClick={() => addAmt(10000)}>1만</button>
          <button type="button" className="zxsb_cart_preset_btn" onClick={() => addAmt(50000)}>5만</button>
          <button type="button" className="zxsb_cart_preset_btn" onClick={() => addAmt(100000)}>10만</button>
          <button type="button" className="zxsb_cart_preset_btn" onClick={() => addAmt(300000)}>30만</button>
          <button type="button" className="zxsb_cart_preset_btn" onClick={() => addAmt(500000)}>50만</button>
          <button type="button" className="zxsb_cart_preset_btn" onClick={() => addAmt('max')}>MAX</button>
        </div>

        {/* Primary Info Rows */}
        <div className="zxsb_cart_info_rows_primary">
          <div className="zxsb_cart_info_row">
            <span className="zxsb_cart_info_label">전체 배당</span>
            <span className="zxsb_cart_info_value">x{formatOdds(displayedFinalOdds || 1.00, _fmt)}</span>
          </div>
          <div className="zxsb_cart_info_row">
            <span className="zxsb_cart_info_label">예상 당첨금액</span>
            <span className="zxsb_cart_info_value">{fmt(Math.floor(displayedPotentialWin || 0))}</span>
          </div>
        </div>

        {selections.length > 0 && (
          <div style={{ marginTop: 4, marginBottom: 8, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
            {bettingLimitsLoading || balanceLoading
              ? '최대 가능 금액 계산 중...'
              : maxStakeInfo.canBetAtLeastMin
              ? `최대 가능 금액: ${fmt(maxStakeInfo.maxStake)}원`
              : `최소 베팅액 ${fmt(maxStakeInfo.limits.global_min_bet)}원 미만 베팅 불가`}
          </div>
        )}

        {/* 베팅하기 CTA Button */}
        <button
          type="button"
          className="zxsb_cart_bet_btn"
          onClick={handlePlace}
          disabled={submitting || selections.length === 0}
        >
          {submitting ? '처리중...' : '베팅하기'}
        </button>
      </div>
    </div>
  );

  const pendingOverlay = pendingBetState ? (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 2000,
      background: 'rgba(3, 14, 19, 0.82)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '360px',
        borderRadius: '18px',
        background: '#142d37',
        border: '1px solid rgba(166, 196, 213, 0.18)',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.48)',
        padding: '24px 22px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: '#f3f8fa' }}>
          인플레이 베팅 확인 중
        </div>
        <div style={{
          marginTop: '14px',
          fontSize: '34px',
          fontWeight: 900,
          color: 'var(--sb-primary)',
          lineHeight: 1,
        }}>
          {pendingBetState.secondsLeft > 0 ? pendingBetState.secondsLeft : '...'}
        </div>
        <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 700, color: '#e7f0f4' }}>
          배당변동 확인중
        </div>
        <div style={{ marginTop: '8px', fontSize: '12px', lineHeight: 1.5, color: '#a6c4d5' }}>
          {pendingBetState.secondsLeft > 0
            ? `${pendingBetState.secondsLeft}초 동안 배당과 마켓 상태를 다시 확인합니다.`
            : '최종 베팅 결과를 확인하고 있습니다.'}
        </div>
        <div style={{ marginTop: '12px', fontSize: '12px', lineHeight: 1.5, color: '#7894a0' }}>
          배당이 변동되거나 마켓이 닫히면 베팅이 거부될 수 있습니다.
        </div>
      </div>
    </div>
  ) : null;

  if (!isMobile) {
    return (
      <>
        {cartPanel}
        {pendingOverlay}
        <BonusRecoveryModal
          show={showBonusRecoveryModal}
          onHide={closeBonusRecoveryModal}
          onConfirm={handleBonusRecoveryConfirm}
          onCancel={closeBonusRecoveryModal}
          gameType="sport"
          bonusgames={bonusGames}
          actionType={bonusActionType}
        />
        <SportsBetSuccessModal
          show={showBetSuccessModal}
          onClose={closeBetSuccessModal}
        />
      </>
    );
  }

  if (typeof document === 'undefined') {
    return pendingOverlay;
  }

  return createPortal(
    <div className="zxsb_mobile_cart_portal_root sportsbook-theme">
      <div
        className={`zxsb_mobile_cart_scrim ${mobileOpen ? 'zxsb_mobile_cart_scrim_open' : ''}`}
        onClick={onMobileClose}
      />
      <div className={`zxsb_mobile_cart_sheet ${mobileOpen ? 'zxsb_mobile_cart_sheet_open' : ''}`}>
        <div className="zxsb_mobile_cart_handle" />
        <div className="zxsb_mobile_cart_head">
          <strong>베팅카트</strong>
          <button type="button" className="zxsb_mobile_cart_close" onClick={onMobileClose} aria-label="베팅카트 닫기">
            <X size={18} />
          </button>
        </div>
        {cartPanel}
      </div>
      {pendingOverlay}
      <BonusRecoveryModal
        show={showBonusRecoveryModal}
        onHide={closeBonusRecoveryModal}
        onConfirm={handleBonusRecoveryConfirm}
        onCancel={closeBonusRecoveryModal}
        gameType="sport"
        bonusgames={bonusGames}
        actionType={bonusActionType}
      />
      <SportsBetSuccessModal
        show={showBetSuccessModal}
        onClose={closeBetSuccessModal}
      />
    </div>,
    document.body
  );
};

export default FigmaBetCart;
